import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, Keyboard, Cpu, Cable, Zap, Monitor, Code, Layers, Eye, Sparkles, Volume2, VolumeX, ChevronRight, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

// ============================================================================
// FONT LOADER
// ============================================================================
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=JetBrains+Mono:wght@300;400;500;600&family=Caveat:wght@400;500;600&display=swap');
  `}</style>
);

// ============================================================================
// SOUND ENGINE
// ============================================================================
class JourneySoundEngine {
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;

  initialize() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  // Electronic ping that rises in pitch for each layer
  playLayerPing(layerIndex: number) {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    const baseFreq = 200 + (layerIndex * 80);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(baseFreq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.5, ctx.currentTime + 0.1);

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  }

  // Key click sound
  playKeyClick() {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    // Click sound
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(150, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.05);

    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.05);
  }

  // Whoosh sound for signal traveling
  playSignalWhoosh() {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(100, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.5);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(200, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.5);

    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.setValueAtTime(0.1, ctx.currentTime + 0.25);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  }

  // Journey complete fanfare
  playJourneyComplete() {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);

        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      }, i * 100);
    });
  }
}

const soundEngine = new JourneySoundEngine();

// ============================================================================
// LAYER DATA
// ============================================================================
interface Layer {
  id: string;
  name: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  time: string;
  description: string;
  details: string[];
  code?: string;
  funFact: string;
}

const layers: Layer[] = [
  {
    id: 'physical',
    name: 'Physical Key',
    subtitle: 'Mechanical Switch',
    icon: <Keyboard className="w-6 h-6" />,
    color: '#f97316',
    time: '~5ms',
    description: 'Spring-loaded switch completes circuit when pressed',
    details: [
      'Matrix scan @ 1000Hz',
      'Debounce delay: 5ms',
      'Key travel: 4mm',
      'Actuation force: 45g'
    ],
    funFact: 'Cherry MX switches are rated for 100 million keypresses'
  },
  {
    id: 'usb',
    name: 'USB Protocol',
    subtitle: 'HID Packet',
    icon: <Cable className="w-6 h-6" />,
    color: '#3b82f6',
    time: '~1ms',
    description: 'Keyboard sends 8-byte HID report over USB',
    details: [
      'Polling rate: 1000Hz',
      'Packet size: 8 bytes',
      'Enter keycode: 0x28',
      'USB 2.0: 480 Mbps'
    ],
    code: '00 00 28 00 00 00 00 00',
    funFact: 'USB HID was designed to replace PS/2 in 1996'
  },
  {
    id: 'interrupt',
    name: 'CPU Interrupt',
    subtitle: 'IRQ Handler',
    icon: <Zap className="w-6 h-6" />,
    color: '#eab308',
    time: '~10μs',
    description: 'USB controller triggers hardware interrupt',
    details: [
      'IRQ 1 → IDT[33]',
      'Context switch: ~10μs',
      'Priority: High',
      'Saves CPU state'
    ],
    funFact: 'Modern CPUs can handle millions of interrupts per second'
  },
  {
    id: 'driver',
    name: 'Kernel Driver',
    subtitle: 'Input Subsystem',
    icon: <Cpu className="w-6 h-6" />,
    color: '#22c55e',
    time: '~100μs',
    description: 'OS kernel processes raw input event',
    details: [
      'Driver: usbhid.ko',
      'Creates input_event',
      'Adds to event queue',
      'Wakes waiting processes'
    ],
    code: 'input_report_key(dev, KEY_ENTER, 1)',
    funFact: 'Linux input subsystem handles 300+ different input devices'
  },
  {
    id: 'os',
    name: 'Window System',
    subtitle: 'Event Dispatch',
    icon: <Layers className="w-6 h-6" />,
    color: '#8b5cf6',
    time: '~50μs',
    description: 'OS routes event to focused application',
    details: [
      'X11/Wayland/Win32',
      'Window focus check',
      'Event translation',
      'IPC to browser'
    ],
    funFact: 'X11 protocol is older than Linux itself (1987)'
  },
  {
    id: 'browser',
    name: 'Browser Process',
    subtitle: 'Event Loop',
    icon: <Monitor className="w-6 h-6" />,
    color: '#ec4899',
    time: '~1ms',
    description: 'Browser receives OS event, queues for renderer',
    details: [
      'Multi-process arch',
      'IPC to renderer',
      'Security sandbox',
      'Event serialization'
    ],
    funFact: 'Chrome uses separate processes to prevent one tab crashing others'
  },
  {
    id: 'javascript',
    name: 'JavaScript Engine',
    subtitle: 'V8 Execution',
    icon: <Code className="w-6 h-6" />,
    color: '#f59e0b',
    time: '~0.1ms',
    description: 'Event handler function executes in V8',
    details: [
      'Event listener lookup',
      'JIT compilation',
      'Callback execution',
      'Microtask queue'
    ],
    code: "document.addEventListener('keydown', handler)",
    funFact: 'V8 can compile JavaScript to machine code in microseconds'
  },
  {
    id: 'dom',
    name: 'DOM Update',
    subtitle: 'Render Pipeline',
    icon: <Layers className="w-6 h-6" />,
    color: '#06b6d4',
    time: '~16ms',
    description: 'Browser recalculates layout and repaints',
    details: [
      'Style calculation',
      'Layout reflow',
      'Paint operations',
      'Compositing layers'
    ],
    funFact: 'A single DOM change can trigger thousands of recalculations'
  },
  {
    id: 'pixel',
    name: 'Pixel Output',
    subtitle: 'Photon Emission',
    icon: <Eye className="w-6 h-6" />,
    color: '#22d3ee',
    time: '~1ns',
    description: 'GPU sends signal to display, photons hit your retina',
    details: [
      'GPU frame buffer',
      'DisplayPort/HDMI',
      'LCD pixel update',
      'Photon → Retina'
    ],
    funFact: 'Light travels 30cm in 1 nanosecond—about the length of your keyboard'
  }
];

// ============================================================================
// KEY CAPTURE COMPONENT
// ============================================================================
interface KeyCaptureProps {
  onKeyPress: (key: string) => void;
}

const KeyCapture: React.FC<KeyCaptureProps> = ({ onKeyPress }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [pressedKey, setPressedKey] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      setPressedKey(e.key);
      soundEngine.playKeyClick();
      setTimeout(() => {
        soundEngine.playSignalWhoosh();
        onKeyPress(e.key);
      }, 300);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onKeyPress]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative">
      {/* Blueprint grid background */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(34, 211, 238, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, rgba(34, 211, 238, 0.1) 0%, transparent 50%)'
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-center"
      >
        <motion.p
          className="text-cyan-400/60 text-sm uppercase tracking-[0.3em] mb-8"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          The Keyboard's Journey
        </motion.p>

        {/* Giant key */}
        <motion.div
          className="relative mx-auto cursor-pointer"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          animate={{
            boxShadow: isHovering
              ? '0 0 60px rgba(34, 211, 238, 0.5), 0 0 120px rgba(34, 211, 238, 0.2)'
              : '0 0 30px rgba(34, 211, 238, 0.3), 0 0 60px rgba(34, 211, 238, 0.1)'
          }}
          style={{
            width: '200px',
            height: '200px',
            borderRadius: '20px',
            background: 'linear-gradient(145deg, #1a2535, #0d1520)',
            border: '2px solid rgba(34, 211, 238, 0.3)',
          }}
        >
          {/* Key surface */}
          <motion.div
            className="absolute inset-3 rounded-xl flex items-center justify-center"
            animate={{
              y: pressedKey ? 4 : 0,
              background: pressedKey
                ? 'linear-gradient(145deg, #0d1520, #1a2535)'
                : 'linear-gradient(145deg, #1e3040, #152030)'
            }}
            transition={{ duration: 0.1 }}
            style={{
              border: '1px solid rgba(34, 211, 238, 0.2)',
            }}
          >
            <AnimatePresence mode="wait">
              {pressedKey ? (
                <motion.span
                  key="pressed"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="text-4xl font-bold text-cyan-400"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {pressedKey.length === 1 ? pressedKey.toUpperCase() : pressedKey}
                </motion.span>
              ) : (
                <motion.span
                  key="prompt"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-2xl text-cyan-400/60"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  _
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Glow effect */}
          <motion.div
            className="absolute inset-0 rounded-xl pointer-events-none"
            animate={{
              opacity: isHovering ? 1 : 0.5
            }}
            style={{
              background: 'radial-gradient(circle at center, rgba(34, 211, 238, 0.1) 0%, transparent 70%)'
            }}
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-xl text-white/60"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          Press any key to begin the journey
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-4 text-sm text-cyan-400/40"
          style={{ fontFamily: 'Caveat, cursive' }}
        >
          ↑ What really happens when you press this?
        </motion.p>
      </motion.div>

      {/* Floating particles */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
          initial={{
            x: Math.random() * (typeof window === 'undefined' ? 1280 : window.innerWidth),
            y: Math.random() * (typeof window === 'undefined' ? 800 : window.innerHeight),
          }}
          animate={{
            y: [null, Math.random() * -200],
            opacity: [0, 0.5, 0],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
};

// ============================================================================
// JOURNEY TIMELINE COMPONENT
// ============================================================================
interface JourneyTimelineProps {
  pressedKey: string;
  onComplete: () => void;
}

const JourneyTimeline: React.FC<JourneyTimelineProps> = ({ pressedKey, onComplete }) => {
  const [activeLayer, setActiveLayer] = useState(-1);
  const [expandedLayer, setExpandedLayer] = useState<string | null>(null);
  const [journeyComplete, setJourneyComplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Animate through layers
  useEffect(() => {
    let timer: NodeJS.Timeout;

    const animateLayer = (index: number) => {
      if (index < layers.length) {
        setActiveLayer(index);
        soundEngine.playLayerPing(index);
        timer = setTimeout(() => animateLayer(index + 1), 400);
      } else {
        setJourneyComplete(true);
        soundEngine.playJourneyComplete();
        onComplete();
      }
    };

    // Start animation after initial delay
    timer = setTimeout(() => animateLayer(0), 500);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div ref={containerRef} className="min-h-screen py-20">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-16 px-6"
      >
        <p
          className="text-cyan-400/60 text-sm uppercase tracking-[0.3em] mb-4"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          Tracing your keypress
        </p>
        <h2
          className="text-4xl md:text-5xl font-bold text-white mb-4"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          The Journey of{' '}
          <span className="text-cyan-400">
            "{pressedKey.length === 1 ? pressedKey.toUpperCase() : pressedKey}"
          </span>
        </h2>
        <p className="text-white/40 max-w-lg mx-auto">
          From your fingertip to photons hitting your retina
        </p>
      </motion.div>

      {/* Timeline */}
      <div className="relative px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
        {/* Connection line */}
        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -translate-y-1/2 hidden lg:block" />

        {/* Layers grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {layers.map((layer, index) => {
            const isActive = index <= activeLayer;
            const isCurrent = index === activeLayer;
            const isExpanded = expandedLayer === layer.id;

            return (
              <motion.div
                key={layer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: isActive ? 1 : 0.3,
                  y: 0,
                  scale: isCurrent ? 1.02 : 1
                }}
                transition={{
                  delay: index * 0.1,
                  duration: 0.3
                }}
                className="relative"
              >
                <motion.div
                  className={`relative rounded-xl p-6 cursor-pointer transition-all ${
                    isExpanded ? 'ring-2 ring-cyan-400/50' : ''
                  }`}
                  onClick={() => isActive && setExpandedLayer(isExpanded ? null : layer.id)}
                  style={{
                    background: isActive
                      ? 'linear-gradient(145deg, rgba(30, 48, 64, 0.8), rgba(21, 32, 48, 0.8))'
                      : 'linear-gradient(145deg, rgba(20, 30, 40, 0.5), rgba(15, 22, 30, 0.5))',
                    border: `1px solid ${isActive ? layer.color + '40' : 'rgba(255,255,255,0.05)'}`,
                  }}
                  whileHover={isActive ? { scale: 1.02 } : {}}
                >
                  {/* Layer number */}
                  <div
                    className="absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      background: isActive ? layer.color : 'rgba(255,255,255,0.1)',
                      color: isActive ? '#000' : 'rgba(255,255,255,0.3)'
                    }}
                  >
                    {index + 1}
                  </div>

                  {/* Pulse effect for current layer */}
                  {isCurrent && (
                    <motion.div
                      className="absolute inset-0 rounded-xl pointer-events-none"
                      animate={{
                        boxShadow: [
                          `0 0 20px ${layer.color}40`,
                          `0 0 40px ${layer.color}60`,
                          `0 0 20px ${layer.color}40`
                        ]
                      }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                  )}

                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="p-3 rounded-lg"
                      style={{
                        background: `${layer.color}20`,
                        color: layer.color
                      }}
                    >
                      {layer.icon}
                    </div>
                    <div className="flex-1">
                      <h3
                        className="text-lg font-semibold text-white"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                      >
                        {layer.name}
                      </h3>
                      <p
                        className="text-sm"
                        style={{
                          color: layer.color,
                          fontFamily: 'JetBrains Mono, monospace'
                        }}
                      >
                        {layer.subtitle}
                      </p>
                    </div>
                    <div
                      className="text-xs px-2 py-1 rounded"
                      style={{
                        background: `${layer.color}20`,
                        color: layer.color,
                        fontFamily: 'JetBrains Mono, monospace'
                      }}
                    >
                      {layer.time}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-white/60 text-sm mb-4">
                    {layer.description}
                  </p>

                  {/* Expand indicator */}
                  {isActive && (
                    <div className="flex items-center gap-2 text-cyan-400/60 text-xs">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span>Click for details</span>
                    </div>
                  )}

                  {/* Expanded content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 mt-4 border-t border-white/10">
                          {/* Details */}
                          <div className="grid grid-cols-2 gap-2 mb-4">
                            {layer.details.map((detail, i) => (
                              <div
                                key={i}
                                className="text-xs px-2 py-1 rounded bg-white/5 text-white/60"
                                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                              >
                                {detail}
                              </div>
                            ))}
                          </div>

                          {/* Code snippet */}
                          {layer.code && (
                            <div
                              className="p-3 rounded bg-black/30 text-xs mb-4 overflow-x-auto"
                              style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                color: layer.color
                              }}
                            >
                              {layer.code}
                            </div>
                          )}

                          {/* Fun fact */}
                          <div
                            className="text-sm italic"
                            style={{
                              fontFamily: 'Caveat, cursive',
                              color: layer.color
                            }}
                          >
                            💡 {layer.funFact}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Journey complete message */}
      <AnimatePresence>
        {journeyComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mt-16 px-6"
          >
            <motion.div
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-cyan-400/10 border border-cyan-400/30"
              animate={{
                boxShadow: [
                  '0 0 20px rgba(34, 211, 238, 0.2)',
                  '0 0 40px rgba(34, 211, 238, 0.4)',
                  '0 0 20px rgba(34, 211, 238, 0.2)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles className="w-5 h-5 text-cyan-400" />
              <span
                className="text-cyan-400 font-medium"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Journey Complete! Scroll down to explore more.
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================================
// LIVE TRACKING MODE
// ============================================================================
const LiveTrackingMode: React.FC = () => {
  const [recentKeys, setRecentKeys] = useState<{key: string, time: number, layers: number[]}[]>([]);
  const [isTracking, setIsTracking] = useState(true);

  useEffect(() => {
    if (!isTracking) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const time = Date.now();

      soundEngine.playKeyClick();

      // Animate through layers
      const newEntry = { key, time, layers: [] as number[] };
      setRecentKeys(prev => [newEntry, ...prev.slice(0, 4)]);

      layers.forEach((_, index) => {
        setTimeout(() => {
          soundEngine.playLayerPing(index);
          setRecentKeys(prev => {
            const updated = [...prev];
            const entryIndex = updated.findIndex(e => e.time === time);
            if (entryIndex >= 0) {
              updated[entryIndex] = {
                ...updated[entryIndex],
                layers: [...updated[entryIndex].layers, index]
              };
            }
            return updated;
          });
        }, (index + 1) * 80);
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTracking]);

  return (
    <div className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Live Tracking Mode
          </h2>
          <p className="text-white/60 mb-6">
            Press any key and watch it travel through all 9 layers in real-time
          </p>
          <button
            onClick={() => setIsTracking(!isTracking)}
            className={`px-4 py-2 rounded-lg text-sm transition-all ${
              isTracking
                ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/30'
                : 'bg-white/10 text-white/60 border border-white/10'
            }`}
          >
            {isTracking ? 'Tracking Active' : 'Tracking Paused'}
          </button>
        </div>

        {/* Layer visualization bar */}
        <div className="mb-8">
          <div className="flex gap-1">
            {layers.map((layer, index) => (
              <div key={layer.id} className="flex-1 text-center">
                <div
                  className="h-2 rounded-full mb-2"
                  style={{ background: `${layer.color}30` }}
                >
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: layer.color }}
                    animate={{
                      width: recentKeys[0]?.layers.includes(index) ? '100%' : '0%'
                    }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
                <span
                  className="text-[10px] text-white/40 hidden md:block"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {index + 1}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent keys */}
        <div className="space-y-3">
          {recentKeys.map((entry, entryIndex) => (
            <motion.div
              key={entry.time}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1 - entryIndex * 0.2, x: 0 }}
              className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <div
                className="w-12 h-12 rounded-lg bg-cyan-400/20 flex items-center justify-center text-cyan-400 font-bold"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {entry.key.length === 1 ? entry.key.toUpperCase() : entry.key.slice(0, 3)}
              </div>
              <div className="flex-1 flex gap-1">
                {layers.map((layer, index) => (
                  <motion.div
                    key={layer.id}
                    className="flex-1 h-6 rounded"
                    animate={{
                      background: entry.layers.includes(index) ? layer.color : `${layer.color}20`,
                      scale: entry.layers.includes(index) && entry.layers.length === index + 1 ? [1, 1.1, 1] : 1
                    }}
                    transition={{ duration: 0.1 }}
                  />
                ))}
              </div>
              <div
                className="text-xs text-white/40"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {entry.layers.length === 9 ? '✓ Complete' : `${entry.layers.length}/9`}
              </div>
            </motion.div>
          ))}

          {recentKeys.length === 0 && (
            <div className="text-center py-12 text-white/40">
              Press any key to see it travel through the system...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STATS SECTION
// ============================================================================
const StatsSection: React.FC = () => {
  const stats = [
    { value: '9', label: 'Layers of Abstraction', color: '#22d3ee' },
    { value: '~23ms', label: 'Total Journey Time', color: '#f97316' },
    { value: '50,000+', label: 'Transistors Involved', color: '#8b5cf6' },
    { value: '3', label: 'Context Switches', color: '#22c55e' },
  ];

  return (
    <div className="py-20 px-6 bg-gradient-to-b from-transparent via-cyan-950/10 to-transparent">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            The Numbers Behind a Keypress
          </h2>
          <p className="text-white/60">
            Your simple keystroke traveled through an incredible amount of complexity
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-6 rounded-xl bg-white/5 border border-white/10"
            >
              <div
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{
                  color: stat.color,
                  fontFamily: 'Space Grotesk, sans-serif'
                }}
              >
                {stat.value}
              </div>
              <div className="text-sm text-white/60">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quote */}
        <motion.blockquote
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p
            className="text-2xl text-white/80 italic mb-4"
            style={{ fontFamily: 'Caveat, cursive' }}
          >
            "Any sufficiently advanced technology is indistinguishable from magic."
          </p>
          <cite className="text-white/40 text-sm">— Arthur C. Clarke</cite>
        </motion.blockquote>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const KeyboardJourneyStudy: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'capture' | 'journey' | 'explore'>('capture');
  const [pressedKey, setPressedKey] = useState<string>('');
  const [soundEnabled, setSoundEnabled] = useState(true);

  const handleKeyPress = useCallback((key: string) => {
    setPressedKey(key);
    setPhase('journey');
  }, []);

  const handleJourneyComplete = useCallback(() => {
    setTimeout(() => setPhase('explore'), 1000);
  }, []);

  useEffect(() => {
    soundEngine.setEnabled(soundEnabled);
  }, [soundEnabled]);

  return (
    <div
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #0d1a2d 50%, #0a1420 100%)',
        fontFamily: 'Space Grotesk, sans-serif'
      }}
    >
      <FontLoader />

      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-8 py-6">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/labs')}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Labs</span>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white transition-colors"
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </motion.button>
      </div>

      {/* Blueprint grid overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(rgba(34, 211, 238, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px'
        }}
      />

      {/* Main content */}
      <AnimatePresence mode="wait">
        {phase === 'capture' && (
          <motion.div
            key="capture"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <KeyCapture onKeyPress={handleKeyPress} />
          </motion.div>
        )}

        {(phase === 'journey' || phase === 'explore') && (
          <motion.div
            key="journey"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <JourneyTimeline
              pressedKey={pressedKey}
              onComplete={handleJourneyComplete}
            />

            {phase === 'explore' && (
              <>
                <StatsSection />
                <LiveTrackingMode />

                {/* Footer */}
                <footer className="py-16 px-6 border-t border-white/10">
                  <div className="max-w-4xl mx-auto text-center">
                    <p className="text-white/40 text-sm mb-4">
                      Every keypress is a miracle of engineering, hidden behind layers of abstraction.
                    </p>
                    <button
                      onClick={() => {
                        setPhase('capture');
                        setPressedKey('');
                      }}
                      className="px-6 py-3 rounded-lg bg-cyan-400/20 text-cyan-400 border border-cyan-400/30 hover:bg-cyan-400/30 transition-all"
                    >
                      Start Another Journey
                    </button>
                  </div>
                </footer>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="keyboard-journey" variant="dark" />
    </div>
  );
};

export default KeyboardJourneyStudy;
